import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ManageTimetable = () => {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [submittedTimetables, setSubmittedTimetables] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [scheduleTypes, setScheduleTypes] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [assemblyTime, setAssemblyTime] = useState("");
  const [assemblyDuration, setAssemblyDuration] = useState(0);
  const [periodDuration, setPeriodDuration] = useState(0);
  const [periodNumber, setPeriodNumber] = useState(0);
  const [lunchBreakDuration, setLunchBreakDuration] = useState(0);
  const [lunchBreakAfterPeriod, setLunchBreakAfterPeriod] = useState(0);

  const [isEditing, setIsEditing] = useState(false);
  const [editingTimetableId, setEditingTimetableId] = useState(null);

  const [timetable, setTimetable] = useState({
    Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: []
  });

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const navigate = useNavigate();

  const labelStyle = "block text-black font-semibold text-[18px] mb-2";
  const inputStyle = "w-full px-3 py-2 border rounded-lg text-[14px] focus:outline-none placeholder:text-[14px] placeholder:text-gray-400";

  // Get current session from localStorage
  const sessionId = localStorage.getItem("currentSessionId"); // Make sure sessionId is stored

  // FETCH BASIC DATA
  useEffect(() => {
    if (!sessionId) return;

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${sessionId}`)
      .then(res => setClasses(res.data))
      .catch(err => console.error("Error fetching classes:", err));

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/timetable/getAllTimetables?sessionId=${sessionId}`)
      .then(res => setSubmittedTimetables(res.data))
      .catch(err => console.error("Error fetching timetables:", err));

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/schooltiming/getAllSchoolTimings?sessionId=${sessionId}`)
      .then(res => setScheduleTypes(res.data))
      .catch(err => console.error("Error fetching schedules:", err));

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeachers?sessionId=${sessionId}`)
      .then(res => setTeachers(res.data))
      .catch(err => console.error("Error fetching teachers:", err));
  }, [sessionId]);

  // FETCH SECTIONS + SUBJECTS BASED ON CLASS
  useEffect(() => {
    if (!selectedClass || !sessionId) {
      setSections([]);
      setSubjects([]);
      return;
    }

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName?sessionId=${sessionId}`)
      .then(res => setSections(Array.isArray(res.data) ? res.data : res.data.sections))
      .catch(err => console.error("Error fetching sections:", err));

axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${encodeURIComponent(selectedClass)}?sessionId=${sessionId}`)

      .then(res => setSubjects(Array.isArray(res.data) ? res.data : res.data.subjects))
      .catch(err => console.error("Error fetching subjects:", err));
  }, [selectedClass, sessionId]);

  // FETCH SCHEDULE DETAILS
  useEffect(() => {
    if (!selectedSchedule || !sessionId) return;

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/schooltiming/getSchoolTimingByScheduleName/${encodeURIComponent(selectedSchedule)}?sessionId=${sessionId}`)
      .then(res => {
        const d = res.data;
        setAssemblyTime(d.assemblyTime);
        setAssemblyDuration(d.assemblyDuration);
        setPeriodDuration(d.periodDuration);
        setPeriodNumber(d.periodNumber);
        setLunchBreakDuration(d.lunchBreakDuration);
        setLunchBreakAfterPeriod(d.lunchBreakAfterPeriod);
      })
      .catch(err => console.error("Error fetching schedule details:", err));
  }, [selectedSchedule, sessionId]);

  const handleTimetableChange = (day, index, key, value) => {
    const updated = [...timetable[day]];
    updated[index] = { ...updated[index], [key]: value };
    setTimetable({ ...timetable, [day]: updated });
  };

  // CALCULATE PERIODS TIME
  const calculateTimeSlots = () => {
    const slots = [];
    const assemblyEnd = new Date(new Date().toDateString() + ' ' + assemblyTime);
    assemblyEnd.setMinutes(assemblyEnd.getMinutes() + assemblyDuration);

    let currentTime = assemblyEnd.toTimeString().slice(0, 5);

    for (let i = 1; i <= periodNumber; i++) {
      const nextTime = new Date(new Date().toDateString() + ' ' + currentTime);
      nextTime.setMinutes(nextTime.getMinutes() + periodDuration);

      slots.push({
        startTime: currentTime,
        endTime: nextTime.toTimeString().slice(0, 5)
      });

      currentTime = nextTime.toTimeString().slice(0, 5);

      if (i === lunchBreakAfterPeriod) {
        const lunchEnd = new Date(nextTime);
        lunchEnd.setMinutes(lunchEnd.getMinutes() + lunchBreakDuration);
        currentTime = lunchEnd.toTimeString().slice(0, 5);
      }
    }
    return slots;
  };

  const timeSlots = calculateTimeSlots();

  const handleEditTimetable = (t) => {
    setSelectedClass(t.className);
    setSelectedSection(t.sectionName);
    setSelectedSchedule(t.scheduleName);

    setPeriodDuration(t.periodDuration);
    setPeriodNumber(t.periodNumber);
    setAssemblyDuration(t.assemblyDuration);
    setAssemblyTime(t.assemblyTime);
    setLunchBreakDuration(t.lunchBreakDuration);
    setLunchBreakAfterPeriod(t.lunchBreakAfterPeriod);

    setTimetable(t.timetable);
    setIsEditing(true);
    setEditingTimetableId(t._id);
  };

  const handleDeleteTimetable = async (id) => {
    if (window.confirm("Are you sure you want to delete this timetable?")) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/timetable/deleteTimetable/${id}?sessionId=${sessionId}`);
        setSubmittedTimetables(prev => prev.filter(t => t._id !== id));
        alert("Timetable deleted successfully!");
      } catch {
        alert("Error deleting timetable");
      }
    }
  };

  const handleSaveTimetable = async () => {
    if (!selectedClass || !selectedSection || !selectedSchedule) {
      alert("Please fill all required fields.");
      return;
    }

    const data = {
      sessionId, // Save sessionId along with timetable
      className: selectedClass,
      sectionName: selectedSection,
      scheduleName: selectedSchedule,
      periodDuration,
      periodNumber,
      assemblyDuration,
      assemblyTime,
      lunchBreakDuration,
      lunchBreakAfterPeriod,
      timetable: Object.fromEntries(
        days.map(day => [
          day,
          timetable[day].map((p, i) => ({
            teacher: p.teacher || '',
            subject: p.subject || '',
            startTime: timeSlots[i]?.startTime || '',
            endTime: timeSlots[i]?.endTime || ''
          }))
        ])
      )
    };

    try {
      if (isEditing) {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/timetable/updateTimetable/${editingTimetableId}`, data);
        alert("Timetable updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/timetable/createTimetable`, data);
        alert("Timetable saved successfully!");
      }
      window.location.reload();
    } catch {
      alert("Error saving timetable");
    }
  };

  return (
    <div className="p-4 md:p-6">

      {/* CLASS / SECTION / SCHEDULE FILTERS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

        <div>
          <label className={labelStyle}>Class:</label>
          <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} className={inputStyle}>
            <option value="">Select Class</option>
            {classes.map(c => (
              <option key={c._id} value={c.className}>{c.className}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelStyle}>Section:</label>
          <select value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} className={inputStyle} disabled={!selectedClass}>
            <option value="">Select Section</option>
            {sections.map(s => (
              <option key={s._id} value={s.sectionName}>{s.sectionName}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelStyle}>Schedule Type:</label>
          <select value={selectedSchedule} onChange={(e) => setSelectedSchedule(e.target.value)} className={inputStyle}>
            <option value="">Select Schedule Type</option>
            {scheduleTypes.map(s => (
              <option key={s._id} value={s.scheduleName}>{s.scheduleName}</option>
            ))}
          </select>
        </div>
      </div>

      {/* TIMETABLE INPUT GRID */}
      {selectedClass && selectedSection && selectedSchedule && (
        <div className="grid md:grid-cols-3 gap-10">
          {days.map(day => (
            <div key={day} className="mb-8">
              <h2 className="text-[18px] font-bold text-black mb-2">{day}</h2>
              <hr className="mb-4" />

              {timeSlots.map((slot, index) => (
                <div key={index} className="mb-4">
                  <label className="text-black font-semibold text-[18px] block mb-1">
                    Period {index + 1} ({slot.startTime} - {slot.endTime}):
                  </label>

                  <div className="flex gap-3">
                    <select
                      className={inputStyle}
                      value={timetable[day][index]?.teacher || ""}
                      onChange={(e) => handleTimetableChange(day, index, "teacher", e.target.value)}
                    >
                      <option value="">Select Teacher</option>
                      {teachers.map(t => (
                        <option key={t._id} value={t.firstName + " " + t.lastName}>
                          {t.firstName} {t.lastName}
                        </option>
                      ))}
                    </select>

                    <select
                      className={inputStyle}
                      value={timetable[day][index]?.subject || ""}
                      onChange={(e) => handleTimetableChange(day, index, "subject", e.target.value)}
                    >
                      <option value="">Select Subject</option>
                      {subjects.map(s => (
                        <option key={s._id} value={s.subjectName}>
                          {s.subjectName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}

            </div>
          ))}

          <button onClick={handleSaveTimetable} className="bg-blue-800 hover:bg-blue-600 text-white px-6 py-2 rounded">
            {isEditing ? "Update Timetable" : "Save Timetable"}
          </button>
        </div>
      )}

      {/* SUBMITTED TIMETABLES TABLE */}
      <div className="mt-6">
        <h2 className="text-[18px] font-bold text-black mb-3">Submitted Timetables</h2>

        <table className="w-full border-collapse border border-gray-300 text-center">
          <thead className="bg-gray-200">
            <tr>
              <th className="p-2 border text-[18px] font-semibold">Sr No.</th>
              <th className="p-2 border text-[18px] font-semibold">Class</th>
              <th className="p-2 border text-[18px] font-semibold">Section</th>
              <th className="p-2 border text-[18px] font-semibold">Schedule Type</th>
              <th className="p-2 border text-[18px] font-semibold">Actions</th>
            </tr>
          </thead>

          <tbody>
            {submittedTimetables.map((t, i) => (
              <tr key={t._id} className="hover:bg-gray-100">
                <td className="border p-2">{i + 1}</td>
                <td className="border p-2">{t.className}</td>
                <td className="border p-2">{t.sectionName}</td>
                <td className="border p-2">{t.scheduleName}</td>

                <td className="border p-2">
                  <div className="flex justify-center gap-3">
                    <button onClick={() => navigate(`/viewTimetable/${t._id}`)} className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded">
                      View
                    </button>
                    <button onClick={() => handleEditTimetable(t)} className="bg-yellow-500 hover:bg-yellow-400 text-white px-3 py-1 rounded">
                      Edit
                    </button>
                    <button onClick={() => handleDeleteTimetable(t._id)} className="bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded">
                      Delete
                    </button>
                  </div>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageTimetable;
